import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MdAttachMoney, MdBusiness, MdGppBad, MdListAlt, MdPerson, MdReceiptLong, MdRefresh } from 'react-icons/md';
import { toast } from 'react-toastify';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { APPRoutes } from '../../Utils/Route';
import { getClaimStatusRecord } from '../../Services/Claim360Service';
import BackButton from '../../Components/Common/BackButton';
import ViewEmptyState from '../../Components/Common/ViewEmptyState';
import { MUTED } from '../../Components/Claim360/StediResultParts';
import DataGrid from '../../Components/Table/DataGrid';
import { FieldGrid, formatDateTime, Section, STATUS_STYLE, SummaryTile } from '../../Components/Claim360/StediViewParts';
import { fieldLabel, flattenFields, formatDate, formatMoney } from '../../Utils/stediUtils';

// Shown in the denial reason card or the hero, so left out of the claim detail cards.
const REASON_KEYS = ['statusCategoryCode', 'statusCategoryCodeValue', 'statusCode', 'statusCodeValue', 'effectiveDate', 'tradingPartnerClaimNumber'];

const isAmount = ([key, value]) => /amount/i.test(key) && value !== null && value !== '' && !Number.isNaN(Number(value));

// One table per list of objects in the claim (e.g. service lines); columns are every field any row carries.
function ObjectTable({ items }) {
    const rows = items.map((item) => Object.fromEntries(flattenFields(item)));
    const labels = [...new Set(rows.flatMap((row) => Object.keys(row)))];
    if (!labels.length) return null;
    const columns = labels.map((label) => ({ id: label, label, minWidth: 140, format: (value) => value ?? '-' }));
    return <DataGrid columns={columns} rows={rows} defaultRowsPerPage={10} getRowId={(_row, index) => index} />;
}

export default function DenialManagementView() {
    const navigate = useNavigate();
    const { id, claimIndex } = useParams();
    const { token, permissions, decodedToken } = useAuth();
    const userPermissionCodes = React.useMemo(
        () => resolveUserPermissionCodes({ permissions, decodedToken }),
        [permissions, decodedToken],
    );
    const canRecheck = permissionHas(userPermissionCodes, PermissionCodes.CLAIM_STATUS_EDIT, decodedToken);
    const canViewList = permissionHas(userPermissionCodes, PermissionCodes.CLAIM_STATUS_LIST, decodedToken);
    const [record, setRecord] = React.useState(null);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        if (!token) return undefined;
        let cancelled = false;
        setLoading(true);
        getClaimStatusRecord(id, token)
            .then((response) => {
                if (!cancelled) setRecord(response?.data ?? null);
            })
            .catch((error) => {
                console.error('Failed to load denied claim:', error);
                if (!cancelled) {
                    setRecord(null);
                    toast.error(error.message || 'Failed to load denied claim.');
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [id, token]);

    if (loading) {
        return (
            <div className="spinner position-absolute d-flex align-items-center justify-content-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    const claim = record?.response?.claims?.[Number(claimIndex)];
    if (!claim) {
        return (
            <ViewEmptyState
                title="Denied Claim Not Found"
                description="This denied claim is no longer available."
                icon={MdGppBad}
                canViewList={canViewList}
                backRoute={APPRoutes.DENIAL_MANAGEMENT}
                backLabel="Back to Denial Management"
            />
        );
    }

    const status = claim.claimStatus ?? {};
    const amounts = Object.entries(status).filter(isAmount);
    const detailFields = flattenFields(Object.fromEntries(
        Object.entries(status).filter((entry) => !REASON_KEYS.includes(entry[0]) && !isAmount(entry)),
    ));
    // Lists of objects elsewhere in the claim, such as service lines.
    const tables = Object.entries(claim).filter(([key, value]) => key !== 'claimStatus'
        && Array.isArray(value) && value.some((item) => item && typeof item === 'object'));
    const payer = record.payerName
        ? `${record.payerName}${record.payerId ? ` (${record.payerId})` : ''}`
        : record.payerId;
    const checkedBy = [record.checkedByFirstName, record.checkedByLastName].filter(Boolean).join(' ');

    return (
        <div className="container-fluid py-4">
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                {canViewList && (
                    <BackButton label="Back to Denial Management" onClick={() => navigate(APPRoutes.DENIAL_MANAGEMENT)} />
                )}
                {canRecheck && (
                    <button
                        type="button"
                        className="btn hc-bg-primary text-white rounded-3 fw-semibold d-inline-flex align-items-center gap-2"
                        onClick={() => navigate(APPRoutes.CLAIM_STATUS_EDIT.replace(':id', id), { state: { claimStatus: record } })}
                    >
                        <MdRefresh size={18} /> Re-check Claim Status
                    </button>
                )}
            </div>

            <div className="p-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-primary">
                <div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
                <div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />
                <div className="row g-4 align-items-center position-relative">
                    <div className="col-lg-8">
                        <span className="px-3 py-2 rounded-pill fw-semibold d-inline-block mb-3" style={{ fontSize: '0.82rem', ...STATUS_STYLE.danger }}>
                            Claim Denied
                        </span>
                        <h2 className="fw-bold text-white mb-2" style={{ fontSize: 'clamp(1.45rem, 2.2vw, 2rem)' }}>
                            {record.patientName || '-'}
                        </h2>
                        <p className="mb-1" style={{ color: 'rgba(var(--hc-white-rgb), 0.78)' }}>
                            Member ID: {record.memberId || '-'} · DOB: {formatDate(record.dateOfBirth)}
                        </p>
                        <p className="mb-0" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>Payer: {payer || '-'}</p>
                    </div>
                    <div className="col-lg-4">
                        <div className="p-3 rounded-4" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.10)' }}>
                            <p className="mb-1 small" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>Payer Claim #</p>
                            <p className="fw-bold text-white mb-3 fs-5 text-break">{status.tradingPartnerClaimNumber || '-'}</p>
                            <p className="mb-1 small" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>Organization</p>
                            <p className="fw-semibold text-white mb-0">{record.organizationName || '-'}</p>
                        </div>
                    </div>
                </div>
            </div>

            <Section icon={MdGppBad} title="Denial Reason" className="mb-4">
                <div className="rounded-4 p-3 p-md-4 d-flex flex-column flex-md-row gap-3 justify-content-between" style={STATUS_STYLE.danger}>
                    <div style={{ minWidth: 0 }}>
                        <div className="d-flex flex-wrap gap-2 mb-2">
                            {status.statusCategoryCode && (
                                <span className="badge rounded-pill px-3 py-2 fw-bold" style={{ background: 'var(--hc-surface)', color: 'inherit' }}>
                                    Category {status.statusCategoryCode}
                                </span>
                            )}
                            {status.statusCode && (
                                <span className="badge rounded-pill px-3 py-2 fw-bold" style={{ background: 'var(--hc-surface)', color: 'inherit' }}>
                                    Status {status.statusCode}
                                </span>
                            )}
                        </div>
                        <div className="fw-bold fs-5 lh-sm mb-1">
                            {status.statusCodeValue || 'The payer gave no detailed reason for this denial.'}
                        </div>
                        {status.statusCategoryCodeValue && (
                            <div className="small fw-semibold" style={{ opacity: 0.85 }}>{status.statusCategoryCodeValue}</div>
                        )}
                    </div>
                    {status.effectiveDate && (
                        <div className="text-md-end flex-shrink-0">
                            <div className="small fw-semibold" style={{ opacity: 0.8 }}>Finalized On</div>
                            <div className="fw-bold">{formatDate(status.effectiveDate)}</div>
                        </div>
                    )}
                </div>
            </Section>

            {amounts.length > 0 && (
                <div className="row g-3 mb-4">
                    {amounts.map(([key, value]) => (
                        <SummaryTile key={key} icon={MdAttachMoney} label={fieldLabel(key)} value={formatMoney(value)} />
                    ))}
                </div>
            )}

            <Section icon={MdReceiptLong} title="Claim Details" className="mb-4">
                <FieldGrid fields={detailFields} colClass="col-12 col-sm-6 col-lg-4" />
            </Section>

            {tables.map(([key, items]) => (
                <Section key={key} icon={MdListAlt} title={fieldLabel(key)} className="mb-4">
                    <ObjectTable items={items.filter((item) => item && typeof item === 'object')} />
                </Section>
            ))}

            <div className="row g-4">
                <div className="col-12 col-lg-6">
                    <Section icon={MdPerson} title="Patient" className="h-100">
                        <FieldGrid fields={[
                            ['Name', record.patientName],
                            ['Date of Birth', formatDate(record.dateOfBirth)],
                            ['Member ID', record.memberId],
                            ['Payer', payer],
                        ]}
                        />
                    </Section>
                </div>
                <div className="col-12 col-lg-6">
                    <Section icon={MdBusiness} title="Check" className="h-100">
                        <FieldGrid fields={[
                            ['Organization', record.organizationName],
                            ['Claims Returned', record.claimCount != null ? String(record.claimCount) : null],
                            ['Checked By', checkedBy],
                            ['Checked On', formatDateTime(record.checkedOn)],
                        ]}
                        />
                    </Section>
                </div>
            </div>

            <p className="small mt-3 mb-0" style={MUTED}>
                From the claim status check of {formatDateTime(record.checkedOn)}. Re-check to get the payer's latest answer.
            </p>
        </div>
    );
}
